import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Footer from '../../components/home/Footer';
import { getCompensationDisplayBlocks } from '../../config/salary';
import './JobDetail.css';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');

// Survives React Strict Mode remounts so one visit = one view
const recentClientViews = new Map();
const CLIENT_VIEW_DEBOUNCE_MS = 8000;

const shouldRecordClientView = (jobId) => {
  const key = String(jobId || '');
  const now = Date.now();
  const last = recentClientViews.get(key) || 0;
  if (now - last < CLIENT_VIEW_DEBOUNCE_MS) return false;
  recentClientViews.set(key, now);
  return true;
};

const isPdfFile = (url = '', fileName = '') =>
  /\.pdf($|\?)/i.test(url) || /\.pdf$/i.test(fileName);

const isOfficeFile = (url = '', fileName = '') =>
  /\.(docx?|xlsx?|pptx?)($|\?)/i.test(url) || /\.(docx?|xlsx?|pptx?)$/i.test(fileName);

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [jdError, setJdError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadJob = async () => {
      setLoading(true);
      setError('');
      setJdError(false);
      try {
        const response = await fetch(`${API_BASE}/jobs/${encodeURIComponent(jobId)}`);
        if (response.ok) {
          const foundJob = await response.json();
          if (cancelled) return;
          setJob(foundJob);

          if (shouldRecordClientView(jobId)) {
            fetch(`${API_BASE}/jobs/${encodeURIComponent(jobId)}/view`, {
              method: 'POST',
            }).catch(() => {});
          }
          return;
        }

        const fallback = await fetch('/data/jobs.json');
        if (!fallback.ok) {
          throw new Error('Failed to load jobs');
        }
        const jobs = await fallback.json();
        const foundJob = jobs.find((j) => j.id === jobId);

        if (cancelled) return;
        if (!foundJob) {
          setError('Job not found');
        } else {
          setJob(foundJob);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading job:', err);
        setError('Failed to load job details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadJob();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const jdProxyUrl = useMemo(() => {
    if (!job?.jdFileUrl || !jobId) return '';
    return `${API_BASE}/jobs/${encodeURIComponent(jobId)}/jd`;
  }, [job?.jdFileUrl, jobId]);

  const jdViewerSrc = useMemo(() => {
    if (!jdProxyUrl || !job) return '';
    const name = job.jdFileName || job.jdFileUrl || '';
    if (isPdfFile(job.jdFileUrl, name)) {
      // Same proxy URL — browsers render application/pdf inline in iframe/object
      return `${jdProxyUrl}#toolbar=1&navpanes=0`;
    }
    if (isOfficeFile(job.jdFileUrl, name)) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(jdProxyUrl)}`;
    }
    return jdProxyUrl;
  }, [jdProxyUrl, job]);

  if (loading) {
    return (
      <div className="job-detail-page">
        <div className="job-detail-container">
          <div className="loading">Loading job details...</div>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="job-detail-page">
        <div className="job-detail-container">
          <div className="error-message">{error || 'Job not found'}</div>
          <button className="btn-back" onClick={() => navigate(-1)}>
            ← Back to jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="job-detail-page">
      <div className="job-detail-container">
        <button className="btn-back" onClick={() => navigate(-1)}>
          ← Back to jobs
        </button>

        <div className="job-header">
          <div className="job-header-main">
            <h1>{job.title}</h1>
            <p className="department">{job.department}</p>
            <div className="job-meta">
              <span className="job-detail-location">{job.location}</span>
              <span className="job-detail-type">{job.type}</span>
            </div>
          </div>
          {(() => {
            const blocks = getCompensationDisplayBlocks(job);
            if (!blocks.length) return null;

            // Collect region columns in consistent order from first block that has them
            const regionCols = [];
            const seen = new Set();
            blocks.forEach((block) => {
              block.regions.forEach((r) => {
                if (!seen.has(r.id)) {
                  seen.add(r.id);
                  regionCols.push({ id: r.id, label: r.label });
                }
              });
            });

            return (
              <div className="job-header-salary">
                <div className="job-header-salary-title">Compensation by arrangement</div>
                <div className="job-salary-table-wrap">
                  <table className="job-salary-table">
                    <thead>
                      <tr>
                        <th scope="col">Employment type</th>
                        {regionCols.map((col) => (
                          <th key={col.id} scope="col">
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {blocks.map((block) => {
                        const byRegion = Object.fromEntries(
                          block.regions.map((r) => [r.id, r.value])
                        );
                        return (
                          <tr key={block.id}>
                            <th scope="row">
                              {block.label}
                              <span className="job-header-salary-period"> · {block.period}</span>
                            </th>
                            {regionCols.map((col) => (
                              <td key={col.id}>{byRegion[col.id] || '—'}</td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="job-content">
          <section className="job-section">
            <h2>About the Role</h2>
            <p className="job-description-text">{job.description}</p>
          </section>

          {job.jdFileUrl ? (
            <section className="job-section job-jd-section">
              <div className="job-jd-header">
                <h2>Job Description</h2>
                {job.jdFileName ? (
                  <span className="job-jd-filename">{job.jdFileName}</span>
                ) : null}
              </div>

              {!jdError ? (
                <div className="job-jd-viewer">
                  <object
                    data={jdViewerSrc}
                    type={
                      isPdfFile(job.jdFileUrl, job.jdFileName || '')
                        ? 'application/pdf'
                        : undefined
                    }
                    className="job-jd-iframe"
                    aria-label={job.jdFileName || 'Job description'}
                  >
                    <iframe
                      title={job.jdFileName || 'Job description'}
                      src={jdViewerSrc}
                      className="job-jd-iframe"
                      onError={() => setJdError(true)}
                    />
                  </object>
                </div>
              ) : (
                <div className="job-jd-missing">
                  Preview unavailable in this browser. Use the link below to open the file.
                </div>
              )}

              <p className="job-jd-fallback">
                <a href={jdProxyUrl} target="_blank" rel="noopener noreferrer">
                  Open JD in a new tab
                </a>
                {' · '}
                <a href={`${jdProxyUrl}?download=1`} rel="noopener noreferrer">
                  Download
                </a>
              </p>
            </section>
          ) : null}

          {Array.isArray(job.skills) && job.skills.length > 0 ? (
            <section className="job-section">
              <h2>Skills</h2>
              <div className="job-skills">
                {job.skills.map((skill) => (
                  <span key={skill} className="job-skill-chip">
                    {skill}
                  </span>
                ))}
              </div>
            </section>
          ) : null}

          {Array.isArray(job.responsibilities) && job.responsibilities.length > 0 ? (
            <section className="job-section">
              <h2>Responsibilities</h2>
              <ul className="job-list">
                {job.responsibilities.map((resp, index) => (
                  <li key={index}>{resp}</li>
                ))}
              </ul>
            </section>
          ) : null}

          {Array.isArray(job.requirements) && job.requirements.length > 0 ? (
            <section className="job-section">
              <h2>Requirements</h2>
              <ul className="job-list">
                {job.requirements.map((req, index) => (
                  <li key={index}>{req}</li>
                ))}
              </ul>
            </section>
          ) : null}

          {Array.isArray(job.benefits) && job.benefits.length > 0 ? (
            <section className="job-section">
              <h2>Benefits</h2>
              <ul className="job-list">
                {job.benefits.map((benefit, index) => (
                  <li key={index}>{benefit}</li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="apply-section">
            <h2>Ready to Apply?</h2>
            <p>
              Click the button below to submit your application. You'll need to provide your LinkedIn profile, GitHub profile, and resume.
            </p>
            <button
              className="btn-apply"
                              onClick={() => navigate(`/startup-jobs/${jobId}/apply`)}
            >
              Apply Now
            </button>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default JobDetail;
